/**
 * Minimal error service for gallery builder
 * Provides consistent error formatting and logging without over-engineering
 */

export const ErrorService = {
  /**
   * Standard error types for categorization
   */
  ERROR_TYPES: {
    NETWORK: 'network',
    VALIDATION: 'validation',
    CONFIGURATION: 'configuration',
    VARIANT: 'variant',
    LOADING: 'loading',
    UNKNOWN: 'unknown'
  },

  /**
   * Format error for consistent structure
   * @param {Error|string} error - Error object or message
   * @param {string} context - Where the error occurred
   * @returns {Object} Formatted error object
   */
  formatError(error, context = 'unknown') {
    // Handle different error input types
    const message = error?.message || error?.toString() || 'An error occurred';
    const type = error?.type || this.ERROR_TYPES.UNKNOWN;

    return {
      message,
      type,
      context,
      timestamp: Date.now(),
      // Include original error for debugging
      original: error
    };
  },

  /**
   * Log error to console with context
   * @param {Object} error - Error to log
   * @param {string} context - Where the error occurred
   */
  logError(error, context) {
    const formatted = typeof error === 'object' && error.timestamp
      ? error // Already formatted
      : this.formatError(error, context);

    // Use appropriate console method based on type
    if (formatted.type === this.ERROR_TYPES.NETWORK) {
      console.warn(`[Gallery ${context}]:`, formatted.message);
    } else {
      console.error(`[Gallery ${context}]:`, formatted.message, formatted.original);
    }
  },

  /**
   * Create user-friendly error message
   * @param {Error|string} error - Error object or message
   * @param {string} fallback - Fallback message if error is cryptic
   * @returns {string} User-friendly message
   */
  getUserMessage(error, fallback = 'Something went wrong. Please try again.') {
    const message = error?.message || error?.toString() || '';

    // Don't expose technical details to users
    if (message.includes('TypeError') ||
        message.includes('undefined') ||
        message.includes('null')) {
      return fallback;
    }

    // Clean up common error messages
    if (message.includes('Network')) {
      return 'Connection error. Please check your internet and try again.';
    }

    if (message.includes('variant') || message.includes('Variant')) {
      return 'Unable to find that configuration. Please try different options.';
    }

    return message || fallback;
  }
};